'use client'

import i18n from "../../../../i18n";
import {I18nextProvider} from "react-i18next";
import React, {createContext, useContext, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {updateAppDirection, updateAppLanguage, updateFontSize} from "@/redux/slices/settings";
import {baseStyles} from "@/components/providers/themeContext/themes";
import Cookies from "js-cookie";
import {AsyncCookie} from "@/components/shared/client/utils";

interface LocaleContextType {
    changeLocale: (key: string) => void;
    changeDirection: (key: string) => void;
    direction: string;
    currentLocale: string
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const useCustomLocale = () => {
    const context = useContext(LocaleContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};

const LocalesProvider = ((props: { children: React.ReactNode }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(true);
    const [mounted, setMounted] = useState(false); // Ensure hydration consistency
    const [direction, setDirection] = useState<string>("ltr");
    const [currentLocale, setCurrentLocale] = useState<string>("en")
    const cookieName = process.env.NEXT_PUBLIC_COOKIE_PREFIX + "_" + "CUSTOM_DATA";

    useEffect(() => {
        setPageFontSize();
        setMounted(true);
        void setLocalesData();
    }, []);

    const setLocalesData = async () => {
        let customData: {
            locale: string;
            dir: string;
        } = JSON.parse(Cookies.get(cookieName) ?? JSON.stringify({theme: 1, dir: 'ltr', locale: "en"}));
        changeDirection(customData.dir);
        await changeLocale(customData.locale);
    }

    const setPageFontSize = () => {
        const customFontSize = 14;
        let font = customFontSize - (window.devicePixelRatio - 1) * customFontSize;
        font = font < customFontSize ? customFontSize : font;
        const el = document.getElementById("root");
        if(el) {
            document.getElementById("root")!.setAttribute('style', `font-size: ${font}px;fontFamily:${baseStyles.fontFamily}`)
        }
        dispatch(updateFontSize(font));
        setLoading(false)
    }

    const changeLocale = async (locale: string) => {
        let customData: {} = JSON.parse(Cookies.get(cookieName) ?? JSON.stringify({theme: 1, dir: 'ltr', locale: "en"}));
        setCurrentLocale(locale);
        await i18n.changeLanguage(locale)
        dispatch(updateAppLanguage(locale));
        customData = {
            ...customData,
            locale
        }
        AsyncCookie.setCookie(cookieName, JSON.stringify(customData), 365)
    }

    const changeDirection = (dir: string) => {
        let customData: {} = JSON.parse(Cookies.get(cookieName) ?? JSON.stringify({theme: 1, dir: 'ltr', locale: "en"}));
        setDirection(dir)
        document.getElementsByTagName('html')[0].setAttribute("dir", dir);
        dispatch(updateAppDirection(dir));
        customData = {
            ...customData,
            dir
        }
        AsyncCookie.setCookie(cookieName, JSON.stringify(customData), 365)
    }

    if (!mounted) return null;

    return <LocaleContext.Provider value={{direction, changeDirection, changeLocale, currentLocale}}>
        <I18nextProvider i18n={i18n}>
            {!loading ? props.children : ""}
        </I18nextProvider>
    </LocaleContext.Provider>
})

export default LocalesProvider;